import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  createUserWithEmailAndPassword, signInWithPopup, updatePassword,
  linkWithCredential, EmailAuthProvider, signOut
} from 'firebase/auth';
import { doc, setDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage, googleProvider } from '../lib/firebase';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  User, Mail, Lock, Upload, MapPin, 
  ChevronLeft, ArrowRight, Eye, EyeOff, Chrome, CheckCircle2, XCircle, AlertCircle, Loader2, Sparkles, Building, Briefcase, KeyRound, Phone
} from 'lucide-react';
import { toast } from 'sonner';

import logoImg from '../assets/logo-semfrase.png';
import planoFundo from "../assets/planodefundo.png"; 

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation(); // Adicionado para escutar o Router State vindo do Login
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  const [errors, setErrors] = useState({});
  const [validating, setValidating] = useState({});

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '',
    cpfCnpj: '', cep: '', street: '', number: '', complement: '',
    neighborhood: '', city: '', state: ''
  });

  const passwordRules = {
    length: formData.password.length >= 8,
    upper: /[A-Z]/.test(formData.password),
    lower: /[a-z]/.test(formData.password),
    number: /[0-9]/.test(formData.password),
  };
  const isPasswordValid = Object.values(passwordRules).every(Boolean);

  // --- CAPTURA E IMPORTAÇÃO DOS DADOS DO GOOGLE EM TEMPO REAL ---
  useEffect(() => {
    if (location.state?.googleData) {
      const { name, email, photoURL } = location.state.googleData;
      setFormData(prev => ({ ...prev, name, email }));
      if (photoURL) setPhotoPreview(photoURL);
    }
  }, [location.state?.googleData]);

  // --- VALIDADORES MATEMÁTICOS ---
  const validateCPF = (c) => {
    c = c.replace(/\D/g, '');
    if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
    let s = 0;
    for (let i = 0; i < 9; i++) s += parseInt(c.charAt(i)) * (10 - i);
    let r = 11 - (s % 11);
    if (r === 10 || r === 11) r = 0;
    if (r !== parseInt(c.charAt(9))) return false;
    s = 0;
    for (let i = 0; i < 10; i++) s += parseInt(c.charAt(i)) * (11 - i);
    r = 11 - (s % 11);
    if (r === 10 || r === 11) r = 0;
    return r === parseInt(c.charAt(10));
  };

  const validateCNPJ = (c) => {
    c = c.replace(/\D/g, '');
    if (c.length !== 14 || /^(\d)\1{13}$/.test(c)) return false;
    let b = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let s = 0;
    for (let i = 0; i < 12; i++) s += parseInt(c.charAt(i)) * b[i];
    let r = s % 11;
    if (parseInt(c.charAt(12)) !== (r < 2 ? 0 : 11 - r)) return false;
    b = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    s = 0;
    for (let i = 0; i < 13; i++) s += parseInt(c.charAt(i)) * b[i];
    r = s % 11;
    return parseInt(c.charAt(13)) === (r < 2 ? 0 : 11 - r);
  };

  // --- MÁSCARAS ---
  const maskPhone = (v) => v.replace(/\D/g, "").substring(0, 11).replace(/^(\d{2})(\d{5})(\d{4}).*/, "($1) $2-$3").replace(/^(\d{2})(\d{4})(\d{0,4}).*/, "($1) $2-$3");
  const maskCpfCnpj = (v) => {
    let r = v.replace(/\D/g, "");
    if (r.length <= 11) return r.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    return r.substring(0, 14).replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  };
  const maskCep = (v) => v.replace(/\D/g, "").substring(0, 8).replace(/(\d{5})(\d)/, "$1-$2");

  const getInputClass = (fieldName) => {
    const base = "h-14 rounded-2xl border-2 transition-all bg-white/80 backdrop-blur-sm shadow-inner font-bold uppercase ";
    if (validating[fieldName]) return base + "border-blue-400 animate-pulse";
    if (errors[fieldName]) return base + "border-red-500 bg-red-50/50";
    if (formData[fieldName] && !errors[fieldName]) return base + "border-green-500 bg-green-50/20";
    return base + "border-slate-100 focus:border-blue-600 focus:bg-white";
  };

  // --- CONFERÊNCIAS AO VIVO ---
  const handlePhoneBlur = async () => {
    const clean = formData.phone.replace(/\D/g, "");
    if (!clean) return;

    if (clean.length < 10) {
      setErrors(p => ({ ...p, phone: true }));
      toast.error("Número de telefone incompleto!");
      return;
    }

    setValidating(p => ({ ...p, phone: true }));
    try {
      const q = query(collection(db, "users"), where("phone", "==", clean), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        setErrors(p => ({ ...p, phone: true }));
        toast.error("Este número de telefone já está sendo usado por outro usuário!");
      } else {
        setErrors(p => ({ ...p, phone: false }));
      }
    } finally { setValidating(p => ({ ...p, phone: false })); }
  };

  const handleCpfCnpjBlur = async () => {
    const clean = formData.cpfCnpj.replace(/\D/g, "");
    if (!clean) return;
    if (clean.length > 0 && clean.length < 11) { setErrors(p => ({ ...p, cpfCnpj: true })); toast.error("CPF ou CNPJ incompleto!"); return; }
    if (!(clean.length === 11 ? validateCPF(clean) : validateCNPJ(clean))) { setErrors(p => ({ ...p, cpfCnpj: true })); toast.error("Documento inválido!"); return; }

    setValidating(p => ({ ...p, cpfCnpj: true }));
    try {
      const q = query(collection(db, "users"), where("cpfCnpj", "==", clean), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) { setErrors(p => ({ ...p, cpfCnpj: true })); toast.error("Este documento já está cadastrado!"); } 
      else { setErrors(p => ({ ...p, cpfCnpj: false })); }
    } finally { setValidating(p => ({ ...p, cpfCnpj: false })); }
  };

  const handleEmailBlur = async () => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) return;
    if (!re.test(formData.email)) { setErrors(p => ({ ...p, email: true })); toast.error("Formato de e-mail inválido!"); return; }
    setValidating(p => ({ ...p, email: true }));
    try {
      const q = query(collection(db, "users"), where("email", "==", formData.email.toLowerCase()), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) { setErrors(p => ({ ...p, email: true })); toast.error("E-mail em uso!"); } 
      else { setErrors(p => ({ ...p, email: false })); }
    } finally { setValidating(p => ({ ...p, email: false })); }
  };

  const handleCepBlur = async () => {
    const clean = formData.cep.replace(/\D/g, "");
    if (clean.length !== 8) return;
    setValidating(p => ({ ...p, cep: true }));
    try {
      const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const data = await res.json();
      if (data.erro) { setErrors(p => ({ ...p, cep: true })); toast.error("CEP não localizado!"); } 
      else {
        setErrors(p => ({ ...p, cep: false }));
        setFormData(p => ({ ...p, street: data.logradouro.toUpperCase(), neighborhood: data.bairro.toUpperCase(), city: data.localidade.toUpperCase(), state: data.uf.toUpperCase() }));
      }
    } finally { setValidating(p => ({ ...p, cep: false })); }
  };

  const handleNameBlur = () => {
    if (formData.name && formData.name.trim().split(' ').length < 2) { setErrors(p => ({ ...p, name: true })); toast.error("Digite seu nome completo!"); } 
    else { setErrors(p => ({ ...p, name: false })); }
  };

// --- CORRIGIDO: APENAS IMPORTA OS DADOS E SEPARA O LOGIN DO FLUXO DE ROTAS ---
  const handleGoogleRegister = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const authUser = result.user;

      // 1. Verifica se esse e-mail já tem conta criada para não deixar duplicar
      const snap = await getDocs(query(collection(db, "users"), where("email", "==", authUser.email), limit(1)));
      if (!snap.empty) { 
        toast.error("Esta conta de e-mail já está cadastrada no sistema. Vá para a tela de Login!"); 
        await signOut(auth); 
        return; 
      }

      // 2. Preenche os campos do formulário na tela com os dados do Google
      setFormData(prev => ({ 
        ...prev, 
        name: authUser.displayName?.toUpperCase() || '', 
        email: authUser.email || '' 
      }));
      
      if (authUser.photoURL) {
        setPhotoPreview(authUser.photoURL.replace('s96-c', 's400-c'));
      }

      // 3. A MÁGICA AQUI: Deslogamos imediatamente o Auth do Firebase.
      // Isso impede que as rotas achem que o cadastro acabou e joguem o usuário para o Dashboard.
      // O formulário continua preenchido na tela esperando o resto dos dados obrigatórios!
      await signOut(auth);

      toast.success("Dados importados do Google! Preencha o resto dos campos obrigatórios.");
    } catch (error) { 
      console.error("Erro ao importar dados do Google:", error);
      toast.error("Erro ao importar dados do Google."); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!photoPreview) return toast.error('A foto de perfil é obrigatória!');
    if (formData.name.trim().split(' ').length < 2) return toast.error("Nome Completo obrigatório!");
    if (Object.values(errors).some(v => v === true)) return toast.error("Existem campos incorretos!");
    
    setLoading(true);
    try {
      let authUser = auth.currentUser;
      if (!authUser) {
        const result = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        authUser = result.user;
      } else {
        const credential = EmailAuthProvider.credential(formData.email, formData.password);
        try { await linkWithCredential(authUser, credential); } catch { await updatePassword(authUser, formData.password); }
      }

      let finalURL = photoPreview;
      if (photoFile) {
        const photoRef = ref(storage, `profile-photos/${authUser.uid}`);
        await uploadBytes(photoRef, photoFile);
        finalURL = await getDownloadURL(photoRef);
      }

      await setDoc(doc(db, 'users', authUser.uid), {
        uid: authUser.uid, name: formData.name, email: formData.email.toLowerCase(),
        phone: formData.phone.replace(/\D/g, ""), cpfCnpj: formData.cpfCnpj.replace(/\D/g, ""),
        addressDetails: { ...formData }, photoURL: finalURL, userType: 'client',
        technicianStatus: 'none', createdAt: new Date().toISOString(),
      });
      navigate('/dashboard');
    } catch (error) { toast.error(error.message); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full flex grid grid-cols-1 lg:grid-cols-12 font-sans bg-white overflow-x-hidden text-slate-900">
      <div className="hidden lg:flex lg:col-span-5 relative bg-[#0a0f1a] flex-col items-center justify-center p-12 text-center overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 opacity-30 transform scale-110" style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-600/15 rounded-full blur-[100px]"></div>
        <div className="relative z-20 w-full flex flex-col items-center">
          <div className="relative inline-block mb-10 group">
              <div className="bg-white p-8 rounded-full shadow-[0_0_80px_rgba(59,130,246,0.3)] border-[12px] border-white/10">
                  <img src={logoImg} alt="Socorre Aí" className="w-40 md:w-48 mx-auto" />
              </div>
          </div>
          <div className="bg-white p-8 rounded-[2.5rem] shadow-2xl w-full max-w-sm mb-10 border border-slate-100">
              <h3 className="text-orange-600 font-black italic uppercase text-lg mb-6 leading-none">Benefícios:</h3>
              <ul className="text-slate-800 text-sm font-semibold space-y-4 text-left">
                  <li className="flex items-center gap-3"><Briefcase className="text-blue-600 w-5 h-5"/> Profissionais de todas as áreas</li>
                  <li className="flex items-center gap-3"><AlertCircle className="text-orange-500 w-5 h-5"/> Atendimento rápido</li>
                  <li className="flex items-center gap-3"><KeyRound className="text-blue-600 w-5 h-5"/> Histórico transparente</li>
                  <li className="flex items-center gap-3"><Building className="text-orange-500 w-5 h-5"/> Rede de profissionais avaliados e de confiança</li>
              </ul>
          </div>
          <h1 className="text-6xl font-black uppercase italic text-white leading-[0.85] tracking-tighter mb-6">FAÇA SEU CADASTRO <br /> E CONHEÇA NOSSOS PROFISSIONAIS.</h1>
        </div>
      </div>

      <div className="w-full lg:col-span-7 flex items-center justify-center p-6 md:p-12 md:p-16 relative bg-slate-50/50">
        <button type="button" onClick={() => { signOut(auth); navigate('/'); }} className="absolute top-8 left-8 flex items-center gap-2 text-[10px] font-black uppercase text-slate-400 hover:text-orange-500 transition-all"><ChevronLeft size={16}/> Voltar ao Início</button>
        <div className="w-full max-w-xl text-left">
           <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
              <header className="flex justify-between items-center mb-8 pb-5 border-b border-slate-100">
                <div className="text-left">
                  <h2 className="text-4xl font-black italic uppercase text-slate-900 tracking-tighter leading-none">Cadastro</h2>
                  <p className="text-slate-500 font-bold italic text-xs uppercase mt-2">Segurança para o seu dia a dia.</p>
                </div>
                <div className="relative">
                    <div className={`w-20 h-20 rounded-full rotate-3 overflow-hidden border-4 transition-all duration-500 ${photoPreview ? 'border-green-500 shadow-lg' : 'border-red-500 shadow-red-100'}`}>
                        {photoPreview ? <img src={photoPreview} className="w-full h-full object-cover" alt="" /> : <div className="w-full h-full bg-slate-50 flex items-center justify-center"><User className="text-slate-200" /></div>}
                    </div>
                    <label htmlFor="photo-up" className="absolute -bottom-2 -right-2 bg-orange-500 text-white p-2 rounded-xl cursor-pointer hover:bg-blue-600 shadow-xl transition-all active:scale-90"><Upload size={14} /><input id="photo-up" type="file" accept="image/*" className="hidden" onChange={(e) => {const f=e.target.files[0]; if(f){setPhotoFile(f); setPhotoPreview(URL.createObjectURL(f))}}} /></label>
                </div>
              </header>

              <Button type="button" onClick={handleGoogleRegister} variant="outline" className="w-full h-14 rounded-2xl border-2 border-slate-200 font-black uppercase italic text-[10px] gap-3 hover:bg-white active:scale-[0.98]">
                <Chrome size={18} className="text-red-500" /> Importar do Google
              </Button>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1 text-left">
                    <Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">Nome Completo *</Label>
                    <Input placeholder="NOME E SOBRENOME" className={getInputClass('name')} value={formData.name} onBlur={handleNameBlur} onChange={e => setFormData({...formData, name: e.target.value.toUpperCase()})} required />
                </div>
                <div className="space-y-1 relative text-left">
                    <Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">E-mail *</Label>
                    <Input placeholder="email@exemplo.com" type="email" className={getInputClass('email')} value={formData.email} onBlur={handleEmailBlur} onChange={e => {setFormData({...formData, email: e.target.value.toLowerCase()}); setErrors(p=>({...p, email:false}))}} required />
                    {validating.email && <Loader2 className="absolute right-4 top-10 animate-spin w-4 h-4 text-blue-500" />}
                </div>
                <div className="space-y-1 relative text-left">
                    <Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">Telefone *</Label>
                    <Input placeholder="(00) 00000-0000" className={getInputClass('phone')} value={formData.phone} onBlur={handlePhoneBlur} onChange={e => {setFormData({...formData, phone: maskPhone(e.target.value)}); setErrors(p=>({...p, phone:false}))}} required />
                    {validating.phone && <Loader2 className="absolute right-4 top-10 animate-spin w-4 h-4 text-blue-500" />}
                </div>
                <div className="space-y-1 relative text-left">
                    <Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">CPF ou CNPJ *</Label>
                    <Input placeholder="000.000.000-00" className={getInputClass('cpfCnpj')} value={formData.cpfCnpj} onBlur={handleCpfCnpjBlur} onChange={e => {setFormData({...formData, cpfCnpj: maskCpfCnpj(e.target.value)}); setErrors(p=>({...p, cpfCnpj:false}))}} required />
                    {validating.cpfCnpj && <Loader2 className="absolute right-4 top-10 animate-spin w-4 h-4 text-blue-500" />}
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2 text-left">
                <div className="col-span-1 space-y-1 relative">
                  <Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">CEP *</Label>
                  <Input placeholder="00000-000" value={formData.cep} onBlur={handleCepBlur} onChange={e => {setFormData({...formData, cep: maskCep(e.target.value)}); setErrors(p=>({...p, cep:false}))}} className={getInputClass('cep')} required />
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">Rua *</Label>
                  <Input placeholder="RUA / LOGRADOURO" value={formData.street} onChange={e => setFormData({...formData, street: e.target.value.toUpperCase()})} className="h-14 rounded-2xl border-none bg-white shadow-inner font-bold uppercase" required />
                </div>
                <div className="col-span-1 space-y-1">
                  <Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">Nº *</Label>
                  <Input placeholder="Nº" value={formData.number} onChange={e => setFormData({...formData, number: e.target.value})} className="h-14 rounded-2xl border-none bg-white shadow-inner font-bold text-center" required />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 p-4 bg-slate-50 rounded-3xl border border-slate-100 shadow-inner text-left">
                <div className="col-span-1 space-y-1"><Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">Complemento</Label><Input placeholder="AP / BLOCO" value={formData.complement} onChange={e => setFormData({...formData, complement: e.target.value.toUpperCase()})} className="h-14 rounded-2xl border-none bg-white shadow-sm font-bold uppercase text-center" /></div>
                <div className="col-span-1 space-y-1"><Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">Bairro</Label><Input disabled value={formData.neighborhood} className="h-14 rounded-2xl bg-slate-100/50 border-none font-black italic text-slate-400 text-center uppercase" /></div>
                <div className="col-span-1 space-y-1"><Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">Cidade</Label><Input disabled value={formData.city} className="h-14 rounded-2xl bg-slate-100/50 border-none font-black italic text-orange-600/50 text-center uppercase" /></div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="relative space-y-1">
                  <Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">Senha *</Label>
                  <Input type={showPass ? "text" : "password"} placeholder="••••••••" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="h-14 rounded-2xl border-none bg-white shadow-inner font-bold pr-12" required />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-4 top-10 text-slate-400 hover:text-blue-600">{showPass ? <EyeOff size={18}/> : <Eye size={18}/>}</button>
                </div>
                <div className="relative space-y-1">
                  <Label className="text-[9px] font-black uppercase ml-2 text-slate-400 italic">Confirmar *</Label>
                  <Input type={showConfirmPass ? "text" : "password"} placeholder="••••••••" value={formData.confirmPassword} onChange={e => setFormData({...formData, confirmPassword: e.target.value})} className={`h-14 rounded-2xl border-2 transition-all shadow-inner font-bold pr-12 ${formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-red-500' : 'border-transparent'}`} required />
                  <button type="button" onClick={() => setShowConfirmPass(!showConfirmPass)} className="absolute right-4 top-10 text-slate-400 hover:text-blue-600">{showConfirmPass ? <EyeOff size={18}/> : <Eye size={18}/>}</button>
                </div>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-sm grid grid-cols-2 gap-2 border border-slate-100">
                {Object.entries(passwordRules).map(([key, val]) => (
                   <div key={key} className={`flex items-center gap-2 text-[8px] font-black uppercase italic ${val ? 'text-green-600' : 'text-slate-300'}`}>{val ? <CheckCircle2 size={12}/> : <XCircle size={12}/>} {key === 'length' ? '8+ Caracteres' : key === 'upper' ? 'Maiúscula' : key === 'lower' ? 'Minúscula' : 'Número'}</div>
                ))}
              </div>

              <Button disabled={loading || !isPasswordValid || Object.values(errors).some(v => v === true)} type="submit" className="w-full h-16 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-2xl shadow-xl active:scale-95 text-[12px] italic uppercase">
                {loading ? <Loader2 className="animate-spin w-5 h-5" /> : "Finalizar Conta"}
              </Button>
              <p className="text-center text-[10px] font-black uppercase text-slate-400 italic">Já tem acesso? <Link to="/login" className="text-blue-600 hover:underline">Fazer Login</Link></p>
           </form>
        </div>
      </div>
    </div>
  );
};

export default Register;